'use client';

import { Button } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import enErrors from '@shared/translations/en-GB/errors.json';
import { FRONTIFY_URL, ThemeProvider } from '@shared/utils';
import { NextIntlClientProvider, useTranslations } from 'next-intl';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { startTransition, useEffect } from 'react';
import './globals.css';

const debug = createDebug({ isEnabled: false });

/**
 * Renders the global error UI with translations.
 * This component is wrapped by NextIntlClientProvider and uses translations.
 * @param onReset - Callback function to handle the "Try again" button click
 */
function GlobalErrorContent({ onReset }: { onReset: () => void }) {
  const t = useTranslations('errors.global');

  return (
    <div className="grid grid-rows-[auto_1fr] min-h-screen">
      <header className="flex justify-start items-center py-8 px-6 md:py-12 md:px-10 md:pb-0">
        <Image src={`${FRONTIFY_URL}nj5HM3BTcwcXiaTa6gya.svg`} alt="Lottoland Logo" width={102} height={28} className="h-8 md:h-10 w-auto dark:hidden" priority />
        <Image src={`${FRONTIFY_URL}io9kP6xMU8YFJnDVRokf.svg`} alt="Lottoland Logo" width={102} height={28} className="h-8 md:h-10 w-auto hidden dark:block" priority />
      </header>
      <main className="ll-grid-container flex items-center justify-center pb-24 md:pb-[5.5rem]">
        <div className="flex flex-col items-center gap-7 text-center px-5 w-full md:max-w-[40rem] mx-auto">
          <div className="relative w-full max-w-[21.875rem] md:max-w-[27.5rem] aspect-[440/300] mb-4">
            <Image src={`${FRONTIFY_URL}Z2A91UjrJWvhQPahjvKL.svg`} alt="Error illustration" fill className="object-contain" priority />
          </div>
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <h1 className="display-m md:display-xl !font-light">{t('title')}</h1>
            <p className="text-foreground-subtle">{t('subtitle')}</p>
          </div>
          <Button onClick={onReset} data-testid="try-again-button">
            {t('button_try_again')}
          </Button>
        </div>
      </main>
    </div>
  );
}

/**
 * Global error boundary component that catches unhandled errors in the application.
 * This component replaces the entire layout when a global error occurs and provides:
 * - Comprehensive error logging with context (browser info, viewport, navigation state)
 * - A user-friendly error UI with translations
 * - A reset mechanism to attempt recovery
 *
 * @param error - The error object that triggered the boundary, may include a digest for tracking
 * @param reset - Optional function to reset the error boundary and attempt to re-render
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset?: () => void }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const errorContext: Record<string, unknown> = {
      // Error details
      message: error.message,
      name: error.name,
      stack: error.stack,
      cause: error.cause,
      digest: error.digest,
      timestamp: new Date().toISOString(),

      // Navigation/routing information
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      pathname: pathname || 'unknown',
      searchParams: searchParams ? Object.fromEntries(searchParams.entries()) : {},
      referrer: typeof document !== 'undefined' ? document.referrer : 'unknown',

      // Browser/environment information
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',

      // Viewport/window information
      viewport:
        typeof window !== 'undefined'
          ? {
              width: window.innerWidth,
              height: window.innerHeight,
              devicePixelRatio: window.devicePixelRatio,
            }
          : null,
    };

    debug.forceError('[GLOBAL ERROR] Global error boundary triggered', errorContext);
  }, [error, pathname, searchParams]);

  /**
   * Handles the reset action when the user clicks "Try again".
   * Refreshes the router to re-fetch server-side data and resets the error boundary.
   * Wrapped in startTransition to ensure non-blocking UI updates.
   */
  function handleReset() {
    startTransition(() => {
      router.refresh();
      if (typeof reset === 'function') {
        reset();
      }
    });
  }

  return (
    <html data-brand="lottoland" lang="en-GB" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body className="antialiased">
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
          <NextIntlClientProvider locale="en-GB" messages={enErrors}>
            <GlobalErrorContent onReset={handleReset} />
          </NextIntlClientProvider>
        </ThemeProvider>
      </body>
    </html>
  );
}
